import React from 'react';
import { css } from '@emotion/react';
import { Icon } from '@frontend/icons';
import { NotificationActions } from '@frontend/types';
import { theme } from '@frontend/theme';
import { Text, NakedUl, styles, Button } from '@frontend/design-system';
import { useNotificationPersonalizationMap } from '../../notification-personalization/notification-personalization';
// import { useMuteAction } from '../../shared/mute-action-context';
import { useExpandedViewContext } from '../../shared/expanded-view-context';
import { useNotificationType } from '../base';

//This makes sure we have backwards compatibility until the user has updated to the latest version of the app
const popsEdgeOpenFeature = window.shell?.featureAvailability?.has?.('pops-edge-open');

export const DefaultNotificationHeaderNew = ({
  title,
  onClose,
}: {
  title: React.ReactNode;
  onClose?: (e: React.MouseEvent<Element, MouseEvent>) => void;
}) => {
  const notificationType = useNotificationType();
  const isExpandedView = useExpandedViewContext();
  const notificationPersonalizationMap = useNotificationPersonalizationMap();
  const { color, icon } = notificationPersonalizationMap(notificationType);
  const headerColor = notificationType === 'ai-receptionist-state' ? theme.colors.neutral90 : color['primary'];
  // const muteNotifications = useMuteAction();

  return (
    <header
      css={[
        css`
          -webkit-user-select: none;
          user-select: none;
          align-items: center;
          border-radius: 0;
          width: 100%;
          color: ${headerColor};
          display: flex;
          align-items: center;
        `,
        !popsEdgeOpenFeature &&
          !isExpandedView &&
          css`
            -webkit-app-region: drag;
          `,
      ]}
    >
      <Icon name={icon} size={16} />
      <Text as='span' size='small' weight='bold' css={{ marginLeft: theme.spacing(1), color: 'inherit' }}>
        {title}
      </Text>
      {/**
       * We will re-enable this when we figure out a better way to handle muting notifications
       */}
      {/* <NakedButton
        onClick={(e) => {
          e.stopPropagation();
          muteNotifications?.(notificationType);
        }}
        css={css`
          margin-left: auto;
          display: flex;
          align-items: center;
          gap: 4px;
          border: 1px solid ${color.progress};
          transition: background-color 0.3s ease-in-out;
          background: white;
          border-radius: ${theme.borderRadius.small};
          padding: ${theme.spacing(0.25, 0.75)};
          :hover {
            background-color: ${color.background};
          }
        `}
      >
        <Icon name='mute-small' />
        <Text
          size='small'
          as='span'
          css={css`
            color: inherit;
          `}
          weight='bold'
        >
          Mute
        </Text>
      </NakedButton> */}
      <Button
        className='notification-close-button'
        iconName='x'
        onClick={(e) => {
          e.stopPropagation();
          onClose?.(e);
        }}
        trackingId={`${notificationType}-notification-pop-close-button-click`}
        size='small'
        css={css`
          margin-left: auto;
          padding: 0;
          height: 24px;
          width: 24px;
          -webkit-app-region: no-drag;
        `}
        variant='secondary'
      />
    </header>
  );
};

export const DefaultNotificationInnerWrapperNew = ({ children }: { children: React.ReactNode }) => {
  return (
    <div
      css={{
        width: '100%',
        marginTop: theme.spacing(1),
      }}
    >
      {children}
    </div>
  );
};

export const DefaultNotificationBodyWrapperNew = ({ children }: { children: React.ReactNode }) => {
  return (
    <Text
      size='medium'
      as='span'
      css={css`
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 3;
        overflow-y: hidden;
        text-align: left;
        text-wrap: pretty;
        margin-top: ${theme.spacing(2)};
      `}
    >
      {children}
    </Text>
  );
};

export const DefaultNotificationBodyNew = ({ name, body }: { body: string; name: string }) => (
  <>
    {name && (
      <Text size='medium' weight='bold'>
        {name}
      </Text>
    )}
    <Text size='medium' css={styles.multilineTruncate(2)}>
      {body}
    </Text>
  </>
);

export const DefaultNotificationActionsNew = ({
  actions,
  notificationType,
}: {
  actions: NotificationActions;
  notificationType: string;
}) => {
  return (
    <NakedUl
      css={css`
        display: flex;
        gap: 8px;
        width: 100%;
        bottom: 0;
        margin: 0;
        align-items: flex-end;
        margin-top: ${theme.spacing(0.5)};
      `}
    >
      {actions.map(({ action, label }, index) => {
        return (
          <li key={label + index}>
            <Button
              css={css`
                padding: 0;
                background: transparent;
                :hover {
                  background: transparent;
                  color: ${theme.colors.primary70};
                }
              `}
              variant='tertiary'
              size='small'
              trackingId={`notification-action-${notificationType}-${label}`}
              onClick={(e) => {
                e.stopPropagation();
                action();
              }}
            >
              {label}
            </Button>
          </li>
        );
      })}
    </NakedUl>
  );
};
